import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle2, Circle, ExternalLink } from 'lucide-react'
import { userApi } from '../api/userApi'
import { profileChecklist } from '../utils/profileChecklist'
import type { User } from '@/types/entities'

interface ProfileWelcomeCardProps {
  user: User
}

export function ProfileWelcomeCard({ user }: ProfileWelcomeCardProps) {
  const checklist = profileChecklist(user)

  const completedCount = checklist.filter((item) => item.completed).length
  const allCompleted = completedCount === checklist.length

  // Don't show the card if profile is complete
  if (allCompleted) return null

  const shopifyStoreUrl = userApi.getShopifyStoreUrl(user.shopifyCollectionHandle)

  return (
    <Card className="shadow-subtle border-primary/20">
      <CardHeader>
        <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
        <CardDescription>
          Complete these steps to make the most of your arthaus experience
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {checklist.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              {item.completed ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={
                    item.completed
                      ? 'text-sm font-medium text-muted-foreground line-through'
                      : 'text-sm font-medium'
                  }
                >
                  {item.label}
                </p>
                <p className="text-xs text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-2">
          <Button asChild className="w-full">
            <Link to="/app/account">Complete Your Profile</Link>
          </Button>
        </div>

        {shopifyStoreUrl && (
          <Alert>
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm">Your arthaus store is ready!</span>
              <a
                href={shopifyStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                View Store
                <ExternalLink className="h-3 w-3" />
              </a>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

